import { Card, CardContent } from "@/components/ui/card";
import { cn, formatINR, formatPercent } from "@/lib/utils";
import { AnimatedValue } from "./AnimatedValue";
import { TrendingUp, TrendingDown, Briefcase, Clock } from "lucide-react";
import type { PortfolioSummary, HoldingsSummary, TopPerformer } from "@/hooks";

interface PortfolioSuccessSummaryProps {
  portfolio: PortfolioSummary;
  holdings: HoldingsSummary;
  topPerformers?: TopPerformer[];
  className?: string;
}

/**
 * Portfolio summary card shown after successful AA import.
 * Shows total value (animated), gain/loss, holdings count, and top 3 holdings.
 */
export function PortfolioSuccessSummary({
  portfolio,
  holdings,
  topPerformers,
  className,
}: PortfolioSuccessSummaryProps) {
  const isPositive = portfolio.totalReturns >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  const top3 = topPerformers?.slice(0, 3);

  return (
    <Card className={cn("w-full max-w-md", className)}>
      <CardContent className="space-y-4 py-6">
        {/* Total portfolio value */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Total Portfolio Value</p>
          <AnimatedValue
            value={portfolio.totalValue}
            format="inr"
            className="text-2xl font-bold text-foreground"
          />
        </div>

        {/* Gain/loss and holdings count */}
        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-1">
            <TrendIcon
              className={cn(
                "h-4 w-4",
                isPositive ? "text-green-600" : "text-red-600"
              )}
            />
            <span
              className={cn(
                "font-medium",
                isPositive ? "text-green-600" : "text-red-600"
              )}
            >
              {formatINR(portfolio.totalReturns)} (
              {formatPercent(portfolio.totalReturnsPercent)})
            </span>
          </div>

          <div className="flex items-center gap-1 text-muted-foreground">
            <Briefcase className="h-4 w-4" />
            <span>
              {holdings.count} {holdings.count === 1 ? "holding" : "holdings"}
            </span>
          </div>
        </div>

        {/* Last updated badge */}
        <div className="flex justify-center">
          <div className="flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>Last Updated: Just now</span>
          </div>
        </div>

        {/* Top 3 holdings preview */}
        {top3 && top3.length > 0 && (
          <div className="space-y-2 border-t pt-4">
            <p className="text-xs font-medium text-muted-foreground">
              Top Holdings
            </p>
            {top3.map((performer) => (
              <div
                key={performer.fundName}
                className="flex items-center justify-between text-sm"
              >
                <span className="truncate pr-4 text-foreground">
                  {performer.fundName}
                </span>
                <span
                  className={cn(
                    "shrink-0 font-medium",
                    performer.returns >= 0 ? "text-green-600" : "text-red-600"
                  )}
                >
                  {formatPercent(performer.returns)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
